import { useTranslation } from "react-i18next";
import { AlertTriangle, AlertCircle, Info, ShieldAlert } from "lucide-react";
import clsx from "clsx";
import type { SurveyorAlert } from "./types";

const SEVERITY_CONFIG: Record<
  string,
  { icon: typeof AlertTriangle; bg: string; border: string; text: string; badge: string }
> = {
  critical: {
    icon: AlertTriangle,
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-800",
    badge: "bg-red-100 text-red-700",
  },
  warning: {
    icon: AlertCircle,
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-800",
    badge: "bg-amber-100 text-amber-700",
  },
  info: {
    icon: Info,
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-800",
    badge: "bg-blue-100 text-blue-700",
  },
};

export const SurveyorAlertsSection = ({
  alerts,
}: {
  alerts?: SurveyorAlert[];
}) => {
  const { t } = useTranslation();

  if (!alerts || alerts.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
          <ShieldAlert size={16} className="text-red-600" />
        </div>
        <h3 className="text-sm font-semibold text-gray-900">
          {t("smartSurveyor.researchReport.surveyorAlerts")}
        </h3>
        <span className="text-xs text-gray-400 ml-auto">
          {alerts.length}
        </span>
      </div>
      <div className="p-4 space-y-2">
        {alerts.map((a, i) => {
          const config = SEVERITY_CONFIG[a.severity] ?? SEVERITY_CONFIG.info;
          const Icon = config.icon;
          return (
            <div
              key={i}
              className={clsx(
                "rounded-lg border px-4 py-3 flex items-start gap-3",
                config.bg,
                config.border,
              )}
            >
              <Icon size={16} className={clsx(config.text, "mt-0.5 shrink-0")} />
              <div className="flex-1 min-w-0">
                <span className={clsx("text-sm", config.text)}>{a.message}</span>
              </div>
              <span
                className={clsx(
                  "text-xs font-semibold px-2 py-0.5 rounded-full shrink-0",
                  config.badge,
                )}
              >
                {t(`smartSurveyor.researchReport.severity.${a.severity}`)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
